import React, { useEffect, useState, useMemo } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useParams, useLocation } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { createInstance } from '../../util/sdkLoader';
import { createServerPaymentIntent } from '../../util/connectPaymentsClient';
import styles from './PaymentPage.module.css';
import { checkProfileCompletion } from '../../util/profileValidation';

// Lazily initialize SDK to keep SSR safe
let __sdkInstance = null;
const getSdk = () => {
  if (__sdkInstance) return __sdkInstance;
  const clientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID
    || process.env.REACT_APP_SHARETRIBE_CLIENT_ID
    || (typeof window !== 'undefined' && window.__PUBLIC_CLIENT_ID__);
  if (!clientId) throw new Error('clientId must be provided');
  __sdkInstance = createInstance({ clientId });
  return __sdkInstance;
};

// Load Stripe outside of render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentPage = () => {
  const params = useParams();
  const location = useLocation();
  let transactionId = params.transactionId;

  if (!transactionId) {
    const searchParams = new URLSearchParams(location.search);
    transactionId = searchParams.get('transactionId');
  }

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Persist a soft success (external payment succeeded but process not yet advanced) across re-renders
  const [scatSoftSuccess, setScatSoftSuccess] = useState(false);
  // Store account mapping state (fetched from backend) for store-based split payouts
  const [storeAccountMapping, setStoreAccountMapping] = useState(null);
  // Alcohol compliance gating
  const [currentUser, setCurrentUser] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(() => {
    try {
      return sessionStorage.getItem('indytend_age_confirmed_21') === 'true';
    } catch (_) { return false; }
  });
  const [showAgeGate, setShowAgeGate] = useState(false);

  // Fetch store account mapping once (feature gated)
  useEffect(() => {
    const USE_STORE_SPLIT = (process.env.REACT_APP_STORE_SPLIT || 'true') !== 'false';
    if (!USE_STORE_SPLIT) return;
    let cancelled = false;
    async function fetchMapping() {
      try {
        const res = await fetch('/api/store-accounts', { credentials: 'include' });
        if (!res.ok) throw new Error('fetch store-accounts ' + res.status);
        const json = await res.json();
        if (cancelled) return;
        if (json && json.mappings) {
          setStoreAccountMapping(json.mappings);
          try { window.__STORE_ACCOUNT_MAPPING_JSON__ = json.mappings; } catch (_) {}
          console.log('[SCaT] store account mappings loaded', { count: Object.keys(json.mappings).length, updated: json.updated });
        }
      } catch (e) {
        if (!cancelled) console.warn('[SCaT][warn] failed to fetch store account mappings', e);
      }
    }
    fetchMapping();
    return () => { cancelled = true; };
  }, []);

  // Fetch transaction details
  useEffect(() => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    console.log('Fetching transaction details for ID:', transactionId);

    getSdk().transactions
      .show({
        id: transactionId,
        include: ['lineItems', 'listing', 'customer', 'provider'],
        expand: true
      })
      .then(response => {
        console.log('Transaction details received:', response.data.data);
        setTransaction(response.data.data);
        setLoading(false);
      })
      .catch(e => {
        console.error('Error fetching transaction:', e);
        setError(e.message || 'Failed to fetch transaction');
        setLoading(false);
      });
  }, [transactionId]);

  // Fetch current user to check ID verification status (cookie-based auth)
  useEffect(() => {
    let cancelled = false;
    async function fetchUser() {
      try {
        const res = await getSdk().currentUser.show();
        if (!cancelled) setCurrentUser(res?.data?.data || null);
      } catch (e) {
        console.warn('Failed to fetch current user for compliance checks', e);
        if (!cancelled) setCurrentUser(null);
      }
    }
    fetchUser();
    return () => { cancelled = true; };
  }, []);

  // Determine if order contains alcohol from line items or protected data
  const hasAlcohol = useMemo(() => {
    const items = transaction?.attributes?.lineItems || [];
    const liAlcohol = items.some(li => li.code === 'line-item/alcohol' && !li.reversal);
    if (liAlcohol) return true;
    const pd = transaction?.attributes?.protectedData || {};
    const otherAddOns = Array.isArray(pd.otherAddOns) ? pd.otherAddOns : [];
    const alcoholCats = new Set(['liquor','beer','wine','champagne','seltzer','alcohol']);
    const addOnsAlcohol = otherAddOns.some(i => alcoholCats.has(String(i?.category || '').toLowerCase()));
    if (addOnsAlcohol) return true;
    const selections = Array.isArray(pd.cocktailSelections) ? pd.cocktailSelections : [];
    const selectionHasAlcohol = selections.some(sel => (sel?.ingredients || []).some(ing => {
      const c = String(ing?.category || '').toLowerCase();
      const nm = String(ing?.name || '').toLowerCase();
      return alcoholCats.has(c) || /(vodka|rum|gin|tequila|whiskey|whisky|bourbon|scotch|brandy|mezcal|liqueur|wine|beer|champagne|prosecco|seltzer)/.test(nm);
    }));
    return selectionHasAlcohol;
  }, [transaction]);

  const RETAILER_NAME = (
    process.env.REACT_APP_LIQUOR_RETAILER_NAME ||
    (process.env.LIQUOR_STORE_ID ? String(process.env.LIQUOR_STORE_ID).replace(/[_-]+/g, ' ') : null) ||
    'Cellars Fine Wine and Spirits'
  );

  // Compute ID verification status using profile validation helper
  const idVerified = useMemo(() => {
    if (!currentUser) return false;
    try {
      const { missingFields } = checkProfileCompletion(currentUser);
      return !missingFields.includes('identificationVerification');
    } catch (_) {
      return false;
    }
  }, [currentUser]);

  // Trigger age gate modal when alcohol is present and not confirmed yet
  useEffect(() => {
    if (hasAlcohol && !ageConfirmed) {
      setShowAgeGate(true);
    }
  }, [hasAlcohol, ageConfirmed]);

  const handleAgeConfirm = () => {
    try { sessionStorage.setItem('indytend_age_confirmed_21', 'true'); } catch (_) {}
    setAgeConfirmed(true);
    setShowAgeGate(false);
  };
  const handleAgeDecline = () => {
    setAgeConfirmed(false);
    setShowAgeGate(false);
    setError('You must be 21 or older to purchase alcohol.');
  };

  // Stripe payment form (full original, with getSdk in place of sdk)
  function StripePaymentForm({ amount, transactionId, transaction, useScatFlow, parentSoftSuccess, registerSoftSuccess, blocked = false, blockedReasons = [] }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentAlreadyAuthorized, setPaymentAlreadyAuthorized] = useState(false);
    const [scatInitialized, setScatInitialized] = useState(false);
    const [scatInitAttempt, setScatInitAttempt] = useState(0);
    // Ensure Airtable dispatch (fallback) only requested once per session
    const [airtableDispatchRequested, setAirtableDispatchRequested] = useState(false);

    // Fallback: once we have a successful payment (any flow) trigger explicit dispatch
    useEffect(() => {
      if (!success) return;
      if (airtableDispatchRequested) return;
      if (!transactionId) return;
      setAirtableDispatchRequested(true);
      (async () => {
        try {
          const resp = await fetch('/api/airtable/dispatch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ transactionId })
          });
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            console.warn('[Payments] Airtable dispatch fallback failed', json);
          } else {
            console.log('[Payments] Airtable dispatch fallback result', json);
          }
        } catch (e) {
          console.warn('[Payments] Airtable dispatch fallback error', e);
        }
      })();
    }, [success, transactionId, airtableDispatchRequested]);

    useEffect(() => {
      const flagged = parentSoftSuccess || transaction?.attributes?.protectedData?.scatCompleted;
      if (flagged && !success) {
        setSuccess(true);
      }
    }, [parentSoftSuccess, transaction, success]);

    const buildServerInputs = React.useCallback(() => {
      if (!transaction) return null;
      try {
        const pd = transaction.attributes?.protectedData || {};
        const pricing = pd.pricing || {};
        const selections = pd.cocktailSelections || [];
        const otherAddOns = pd.otherAddOns || [];
        const lineItems = transaction.attributes?.lineItems || [];
        const providerAccountId = pd.providerConnectAcctId || pd.providerConnectAccountId || pd.providerStripeAccountId || pd.providerStripeConnectId || null;
        const glasswareConnectAccountId = pd.glasswareConnectAccountId || null;
        const flags = {
          storeAllocMode: ((process.env.REACT_APP_STORE_ALLOC_MODE || '').split(/\s|#/)[0]) || 'budget-capped',
          storeMultiplier: process.env.REACT_APP_STORE_MULTIPLIER,
          storeMultiplyByQty: true,
          storeIncludeIngQty: true,
          preferLargeMixerBottles: true,
          ...(pd.flags || {}),
        };
        const storeAccounts = storeAccountMapping || {};

        const OZ_TO_ML = 29.5735;
        const defaultBottleSizeMl = (ing) => {
          const nm = String(ing?.name || '').toLowerCase();
          const cid = String(ing?.catalogId || '').toLowerCase();
          if (cid.startsWith('juice_') || /juice/.test(nm)) return 1000;
          if (/(club\s*soda|seltzer|sparkling\s*water|tonic|ginger\s*(ale|beer)|soda)/.test(nm)) return 1000;
          if (/(wine|champagne|prosecco)/.test(nm)) return 750;
          if (/(beer|ipa|lager|stout|pils)/.test(nm)) return 355;
          if (/(vodka|rum|gin|tequila|whiskey|whisky|bourbon|scotch|brandy|mezcal|liqueur)/.test(nm) || /(vodka|rum|gin|tequila|whiskey|whisky|bourbon|scotch|brandy|mezcal|liqueur)/.test(cid)) return 750;
          if (/syrup/.test(nm)) return 750;
          return null;
        };
        const enrichedSelections = (Array.isArray(selections) ? selections : []).map(sel => ({
          ...sel,
          ingredients: (sel.ingredients || []).map(ing => {
            const out = { ...ing };
            const explicitBottles = parseFloat(out.bottles ?? out.bottlesNeeded ?? out.bottles_needed);
            if (Number.isFinite(explicitBottles) && explicitBottles > 0) {
              out.bottles = explicitBottles;
            }
            const unit = String(out.unit || '').toLowerCase();
            const maybeQtyOz = parseFloat(out.amountPerCocktailOz || 0) || 0;
            if ((!maybeQtyOz || maybeQtyOz <= 0) && unit === 'oz') {
              const parsed = parseFloat(out.qty || out.quantity || 0);
              if (Number.isFinite(parsed) && parsed > 0) {
                out.amountPerCocktailOz = parsed;
              }
            }
            const hasBottleSize = Number.isFinite(parseFloat(out.bottleSizeMl || out.bottle_size_ml || out.volume_ml || out.volumeMl)) && parseFloat(out.bottleSizeMl || out.bottle_size_ml || out.volume_ml || out.volumeMl) > 0;
            if (!hasBottleSize) {
              const def = defaultBottleSizeMl(out);
              if (def) out.bottleSizeMl = def;
            }
            if (out.pricePerUnit == null && out.calculatedCost != null) {
              out.pricePerUnit = out.calculatedCost;
            }
            return out;
          })
        }));

        return { pricing, selections: enrichedSelections, otherAddOns, lineItems, providerAccountId, glasswareConnectAccountId, flags, storeAccounts };
      } catch (e) {
        console.warn('[SCaT] build server inputs failed', e);
        return null;
      }
    }, [transaction, storeAccountMapping]);

    useEffect(() => {
      let cancelled = false;
      async function initScat() {
        if (!useScatFlow || scatInitialized || clientSecret) return;
        if (!storeAccountMapping) {
          console.log('[SCaT] Waiting for store account mappings before initializing PaymentIntent');
          return;
        }
        if (parentSoftSuccess || transaction?.attributes?.protectedData?.scatCompleted) {
          setScatInitialized(true);
          return;
        }
        if (!transactionId || !transaction) return;
        const inputs = buildServerInputs();
        if (!inputs) {
          setScatInitialized(true);
          return;
        }
        try {
          setProcessing(true);
          const { pricing, selections, otherAddOns, lineItems, providerAccountId, glasswareConnectAccountId, flags, storeAccounts } = inputs;
          const listingId = transaction?.relationships?.listing?.data?.id?.uuid || transaction?.attributes?.listing?.id?.uuid || null;
          const providerId = transaction?.relationships?.provider?.data?.id?.uuid || transaction?.attributes?.provider?.id?.uuid || null;
          const { clientSecret: cs } = await createServerPaymentIntent({
            transactionId,
            pricing,
            selections,
            otherAddOns,
            lineItems,
            storeAccounts,
            flags,
            providerAccountId,
            glasswareConnectAccountId,
            listingId,
            providerId,
          });
          if (!cancelled) {
            setClientSecret(cs);
            setScatInitialized(true);
            setProcessing(false);
          }
        } catch (e) {
          console.error('[SCaT] initialization failed, will attempt legacy discovery', e);
          if (!cancelled) setProcessing(false);
        }
      }
      initScat();
      return () => { cancelled = true; };
    }, [useScatFlow, scatInitialized, clientSecret, transactionId, transaction, buildServerInputs, scatInitAttempt, storeAccountMapping]);

    const findClientSecret = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      if (obj.client_secret) return obj.client_secret;
      if (obj.id && typeof obj.id === 'string' && obj.id.startsWith('pi_') && obj.status) {
        for (const key in obj) {
          if (typeof obj[key] === 'string' &&
              (key.includes('secret') ||
               obj[key].includes('_secret_') ||
               obj[key].startsWith('pi_') ||
               obj[key].includes('seti_'))) {
            return obj[key];
          }
        }
      }
      for (const key in obj) {
        if (['attributes', 'relationships', 'id', 'type'].includes(key)) continue;
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          const found = findClientSecret(obj[key]);
          if (found) return found;
        }
        if (typeof obj[key] === 'string' &&
            (obj[key].includes('client_secret') ||
             obj[key].includes('intent') ||
             obj[key].includes('pi_'))) {
          try {
            const parsed = JSON.parse(obj[key]);
            const found = findClientSecret(parsed);
            if (found) return found;
          } catch (e) {}
        }
      }
      return null;
    };

    // Legacy discovery path for client secret
    useEffect(() => {
      if (!transactionId) return;
      if (useScatFlow && scatInitialized && clientSecret) return;
      if (useScatFlow && !scatInitialized) return;
      if (clientSecret) return;
      setError(null);
      console.log('[Payments] Legacy discovery fetching payment intent for transaction:', transactionId, { useScatFlow, scatInitialized });
      getSdk().transactions
        .show({ id: transactionId })
        .then(res => {
          console.log('Transaction data received:', res.data.data);
          const transactionData = res.data.data;
          const protectedData = transactionData.attributes.protectedData || {};
          let intent = null;
          if (protectedData.pricingPayment?.clientSecret) {
            intent = protectedData.pricingPayment.clientSecret;
          }
          if (protectedData.stripePaymentIntents?.default?.stripePaymentIntentClientSecret) {
            intent = protectedData.stripePaymentIntents.default.stripePaymentIntentClientSecret;
          }
          if (!intent) {
            intent = protectedData.stripePaymentIntentClientSecret || protectedData.paymentIntent?.client_secret || protectedData.paymentIntentClientSecret;
          }
          if (!intent && protectedData.stripePaymentIntents) {
            if (protectedData.stripePaymentIntents.default) {
              const defaultIntent = protectedData.stripePaymentIntents.default;
              if (typeof defaultIntent === 'object' && defaultIntent.client_secret) {
                intent = defaultIntent.client_secret;
              } else if (typeof defaultIntent === 'string') {
                try { const parsedIntent = JSON.parse(defaultIntent); intent = parsedIntent.client_secret; } catch {}
              }
            } else {
              for (const key in protectedData.stripePaymentIntents) {
                const intentData = protectedData.stripePaymentIntents[key];
                if (typeof intentData === 'object' && intentData.client_secret) { intent = intentData.client_secret; break; }
                else if (typeof intentData === 'string' && (intentData.includes('client_secret') || intentData.includes('pi_'))) {
                  try { const parsed = JSON.parse(intentData); if (parsed.client_secret) { intent = parsed.client_secret; break; } } catch {}
                }
              }
            }
          }
          if (!intent) intent = findClientSecret(transactionData);
          if (intent) {
            setClientSecret(intent);
            if (stripe) {
              const checkPaymentIntentStatus = async () => {
                try {
                  const { paymentIntent } = await stripe.retrievePaymentIntent(intent);
                  if (paymentIntent && paymentIntent.status === 'requires_capture') setPaymentAlreadyAuthorized(true);
                } catch {}
              };
              checkPaymentIntentStatus();
            }
          } else {
            setError('No payment information found. The booking must be accepted by the bartender first to create a payment intent.');
          }
        })
        .catch(err => { setError(err.message || 'Failed to load payment details.'); });
    }, [transactionId, useScatFlow, scatInitialized, clientSecret]);

    // SCaT: backend confirm helper
    const backendScatConfirm = async (paymentIntentId, reason) => {
      console.log(`[SCaT] Backend confirm invoked (${reason}) with PI ${paymentIntentId}`);
      try {
        const resp = await fetch('/api/scat/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ transactionId, paymentIntentId })
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          console.warn('[SCaT] Backend confirm non-OK', resp.status, json);
          return { success: false, json, status: resp.status };
        }
        console.log('[SCaT] Backend confirm response', json.info || json.warning || json.message || 'ok');
        try {
          const refreshed = await getSdk().transactions.show({ id: transactionId });
          const st = refreshed?.data?.data?.attributes?.state;
          console.log('[SCaT] Post-confirm state:', st);
          setTransaction(refreshed.data.data);
          if (st && st.includes('accepted')) return { success: true, state: st };
          return { success: true, state: st };
        } catch (rErr) {
          console.warn('[SCaT] Refresh after backend confirm failed', rErr);
          return { success: true, state: 'unknown' };
        }
      } catch (err) {
        console.error('[SCaT] Backend confirm error', err);
        return { success: false, error: err };
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setProcessing(true);
      setError(null);

      if (!stripe || !elements) {
        setError('Stripe has not loaded yet.');
        setProcessing(false);
        return;
      }
      if (!clientSecret) {
        setError('Payment is not ready. Please try again later or refresh the page.');
        setProcessing(false);
        return;
      }

      try {
        const clientSecretMatches = clientSecret.match(/^(pi_[^_]+)_secret/);
        if (clientSecretMatches && clientSecretMatches[1]) {
          const paymentIntentId = clientSecretMatches[1];
          try {
            const { paymentIntent: existingIntent } = await stripe.retrievePaymentIntent(clientSecret);
            if (existingIntent && existingIntent.status === 'requires_capture') {
              if (useScatFlow) {
                const backend = await backendScatConfirm(existingIntent.id, 'requires_capture');
                if (backend.success) {
                  setSuccess(true);
                  setProcessing(false);
                  return;
                }
              }
            }
          } catch {}
        }

        const cardElement = elements.getElement(CardElement);
        let preCheckIntent = null;
        try {
          const retrieved = await stripe.retrievePaymentIntent(clientSecret);
          preCheckIntent = retrieved?.paymentIntent || null;
        } catch {}

        let paymentIntent;
        let stripeError;
        if (preCheckIntent && (preCheckIntent.status === 'succeeded' || preCheckIntent.status === 'requires_capture')) {
          paymentIntent = preCheckIntent;
        } else {
          ({ paymentIntent, error: stripeError } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
          }));
        }

        if (stripeError) {
          if (stripeError.code === 'payment_intent_unexpected_state') {
            const { paymentIntent: checkIntent } = await stripe.retrievePaymentIntent(clientSecret);
            if (checkIntent && checkIntent.status === 'requires_capture') {
              try {
                const backend = await backendScatConfirm(checkIntent.id, 'requires_capture');
                if (backend.success) {
                  setSuccess(true);
                  const refreshResponse = await getSdk().transactions.show({ id: transactionId });
                  setTransaction(refreshResponse.data.data);
                  setProcessing(false);
                  return;
                }
              } catch (err) {
                // fall through
              }
              setProcessing(false);
              return;
            }
          }
          setError(stripeError.message);
          setProcessing(false);
          return;
        }

        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture' || paymentIntent.status === 'processing')) {
          try {
            if (useScatFlow) {
              const backend = await backendScatConfirm(paymentIntent.id, paymentIntent.status);
              if (backend.success) {
                setSuccess(true);
                if (!transaction?.attributes?.protectedData?.scatCompleted) {
                  registerSoftSuccess && registerSoftSuccess();
                }
                setProcessing(false);
                return;
              }
              setSuccess(true);
            } else {
              setSuccess(true);
            }
            try {
              const refreshResponse = await getSdk().transactions.show({ id: transactionId });
              setTransaction(refreshResponse.data.data);
            } catch (_) {}
          } catch (err) {
            setError('Payment succeeded, but failed to finalize booking: ' + (err.message || String(err)));
          }
        } else {
          setError('Payment processing failed. Please try again.');
        }
      } catch (err) {
        setError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      }
      setProcessing(false);
    };

    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
        {!paymentAlreadyAuthorized ? (
          <>
            <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
            <button 
              type="submit" 
              disabled={!stripe || processing || success || blocked} 
              className={styles.paymentButton}
              style={{
                background: '#635bff',
                cursor: (!stripe || processing || success || blocked) ? 'not-allowed' : 'pointer',
                opacity: (!stripe || processing || success || blocked) ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {processing ? (
                <span className={styles.processingText}>
                  <span className={styles.spinner}></span>
                  Processing...
                </span>
              ) : success ? 'Payment Complete' : `Pay $${amount.toFixed(2)}`}
            </button>
          </>
        ) : (
          <>
            <div style={{ 
              padding: '1em', 
              background: '#efffef', 
              border: '1px solid #cce6cc', 
              borderRadius: '4px', 
              marginBottom: '1em',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ margin: '0 0 0.5em 0', color: '#2d8d47', display: 'flex', alignItems: 'center' }}>
                <span role="img" aria-label="authorized" style={{ marginRight: '8px' }}>✅</span>
                Payment Already Authorized
              </h3>
              <p style={{ margin: '0.5em 0' }}>Your payment has already been authorized and just needs to be completed.</p>
              <p style={{ margin: '0.5em 0', fontSize: '0.9em', fontStyle: 'italic' }}>Click the button below to complete your booking and confirm your reservation.</p>
            </div>
            <button 
              type="submit" 
              disabled={processing || success || blocked} 
              className={styles.paymentButton}
              style={{ 
                background: '#4caf50',
                cursor: (processing || success || blocked) ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              {processing ? (
                <span className={styles.processingText}>
                  <span className={styles.spinner}></span>
                  Processing...
                </span>
              ) : success ? 'Payment Complete' : 'Complete Authorized Payment'}
            </button>
          </>
        )}

        {blocked && (
          <div className={styles.notice} style={{ marginTop: '0.75em' }}>
            <strong>Action required:</strong>
            <ul style={{ margin: '0.5em 0 0 1.2em' }}>
              {blockedReasons.map((r, i) => (<li key={i}>{r}</li>))}
            </ul>
          </div>
        )}
        
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        
        {success && (
          <div className={styles.successMessage}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#2d8d47' }}>
              <span role="img" aria-label="checkmark">✔</span> Payment Successful!
            </h3>
            <p style={{ margin: '0.5rem 0' }}>Your booking has been confirmed and payment has been processed successfully.</p>
            
            {transaction && transaction.attributes && (
              <div style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
                <p style={{ margin: '0.25rem 0', fontWeight: 'bold' }}>Transaction Details:</p>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                  <li>Status: <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                    {transaction.attributes.state?.replace('state/', '')?.replace('transition/', '') || 'Processed'}
                  </span></li>
                  <li>Transaction ID: <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {transaction.id?.uuid || 'Unknown'}
                  </span></li>
                  <li>Processed at: {new Date().toLocaleString()}</li>
                </ul>
              </div>
            )}
            
            <p style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.9rem', fontStyle: 'italic' }}>
              A confirmation email will be sent shortly with your booking details.
            </p>
            
            <div style={{ marginTop: '1rem' }}>
              <button 
                onClick={() => window.location.href = '/inbox'}
                className={styles.viewBookingsButton}
              >
                View My Bookings
              </button>
              {transaction?.attributes?.state?.includes('pending-payment') && USE_SCAT_FLOW && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setProcessing(true);
                      const resp = await fetch('/api/scat/confirm', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ transactionId, paymentIntentId: (clientSecret||'').split('_secret_')[0] })
                      });
                      const json = await resp.json();
                      if (resp.ok) {
                        const refreshed = await getSdk().transactions.show({ id: transactionId });
                        setTransaction(refreshed.data.data);
                        setError(null);
                      } else {
                        setError('Manual SCaT confirm failed: ' + (json.error || 'Unknown'));
                      }
                    } catch (e) {
                      setError('Manual SCaT confirm error: ' + (e.message||e));
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  style={{ marginLeft: '0.75rem', background: '#635bff', color: '#fff', padding: '0.5em 0.9em', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Force Confirm (SCaT)
                </button>
              )}
            </div>
          </div>
        )}
        
        {!clientSecret && !processing && (
          <div style={{ marginTop: '1em', padding: '1em', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #ddd' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5em' }}>
              <strong>Troubleshooting:</strong> If you're seeing errors during payment, try the following:
            </p>
            <ol style={{ fontSize: '14px', marginLeft: '1.5em', marginBottom: '1em' }}>
              <li>Make sure the booking has been accepted by the bartender</li>
              <li>Verify the transaction is in "pending-payment" state (you're seeing this payment form, so this should be OK)</li>
              <li>If you see an error mentioning "state/pending-payment", our system will now automatically handle this state prefix inconsistency. Simply try the payment again.</li>
              <li>If you receive an "invalid transition" error, the booking may already be processed and in a different state. Check your transactions page.</li>
              <li>If you see a 409 Conflict error, this often means the transaction has already moved to the next state in the process (which is good!)</li>
              <li>If you're still having issues, try the "Check for Payment Intent" button below</li>
            </ol>
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <button 
                type="button"
                onClick={() => window.location.reload()}
                className={styles.refreshButton}
              >
                Refresh Page
              </button>
              
              <button 
                type="button"
                onClick={async () => {
                  try {
                    const stateCheckResponse = await getSdk().transactions.show({ 
                      id: transactionId,
                      include: ['lineItems', 'listing', 'customer', 'provider'],
                      expand: true
                    });
                    const txState = stateCheckResponse?.data?.data?.attributes?.state;
                    if (txState && (
                        txState.includes('complete') || 
                        txState.includes('paid') || 
                        txState.includes('delivered')
                    )) {
                      setSuccess(true);
                      setTransaction(stateCheckResponse.data.data);
                      setProcessing(false);
                      return;
                    }
                    setError(null);
                    setProcessing(true);
                    const response = await getSdk().transactions.show({ 
                      id: transactionId,
                      include: ['lineItems', 'listing', 'customer', 'provider'],
                      expand: true
                    });
                    const transactionData = response.data.data;
                    const protectedData = transactionData.attributes.protectedData || {};
                    const txCurrentState = transactionData.attributes.state;
                    if (txCurrentState && (
                        txCurrentState.includes('complete') || 
                        txCurrentState.includes('paid') || 
                        txCurrentState.includes('delivered')
                    )) {
                      setSuccess(true);
                      setTransaction(response.data.data);
                      setProcessing(false);
                      setError(null);
                      return;
                    }
                    if (txCurrentState && txCurrentState.includes('pending-payment')) {
                      if (txCurrentState.startsWith('state/')) {
                        setError(
                          <div style={{ color: '#856404', backgroundColor: '#fff3cd', padding: '0.75em', borderRadius: '4px', marginBottom: '1em', border: '1px solid #ffeeba' }}>
                            <p><strong>Note:</strong> This transaction is using "state/pending-payment" format.</p>
                            <p>We've enabled special handling for this format. If you encounter any issues, please try the "Complete Authorized Payment" button.</p>
                          </div>
                        );
                      }
                      if (intent) {
                        try {
                          const { paymentIntent } = await stripe.retrievePaymentIntent(intent);
                          if (paymentIntent && paymentIntent.status === 'requires_capture') {
                            setPaymentAlreadyAuthorized(true);
                          }
                        } catch (err) {}
                      }
                    }
                    let intent = null;
                    if (protectedData.stripePaymentIntents?.default?.stripePaymentIntentClientSecret) {
                      intent = protectedData.stripePaymentIntents.default.stripePaymentIntentClientSecret;
                    }
                    if (!intent) {
                      intent = protectedData.stripePaymentIntentClientSecret || 
                              protectedData.paymentIntent?.client_secret ||
                              protectedData.paymentIntentClientSecret;
                    }
                    if (!intent && protectedData.stripePaymentIntents) {
                      if (protectedData.stripePaymentIntents.default) {
                        const defaultIntent = protectedData.stripePaymentIntents.default;
                        if (typeof defaultIntent === 'object' && defaultIntent !== null) {
                          if (defaultIntent.client_secret) {
                            intent = defaultIntent.client_secret;
                          }
                          if (!intent) {
                            for (const key in defaultIntent) {
                              if (typeof defaultIntent[key] === 'string') {
                                if (key === 'client_secret' || key.includes('secret')) {
                                  intent = defaultIntent[key];
                                  break;
                                }
                                if (defaultIntent[key].includes('_secret_')) {
                                  intent = defaultIntent[key];
                                  break;
                                }
                              }
                            }
                          }
                        } else if (typeof defaultIntent === 'string') {
                          if (defaultIntent.includes('client_secret')) {
                            try {
                              const parsed = JSON.parse(defaultIntent);
                              if (parsed.client_secret) {
                                intent = parsed.client_secret;
                              }
                            } catch (e) {
                              const match = defaultIntent.match(/client_secret["\s:=]+([^"',\s]+)/);
                              if (match && match[1]) {
                                intent = match[1];
                              }
                            }
                          }
                        }
                      } else {
                        for (const key in protectedData.stripePaymentIntents) {
                          const intentData = protectedData.stripePaymentIntents[key];
                          if (intentData && typeof intentData === 'object' && intentData.client_secret) {
                            intent = intentData.client_secret;
                            break;
                          }
                        }
                      }
                    }
                    if (!intent) {
                      try {
                        const findInObject = (obj, depth = 0) => {
                          if (!obj || typeof obj !== 'object' || depth > 5) return null;
                          if (obj.client_secret && typeof obj.client_secret === 'string') {
                            return obj.client_secret;
                          }
                          for (const key in obj) {
                            if (typeof obj[key] === 'string') {
                              if (key === 'client_secret' || key.includes('secret')) {
                                return obj[key];
                              }
                              const value = obj[key];
                              if (value.startsWith('pi_') && value.includes('_secret_')) {
                                return value;
                              }
                            }
                            if (typeof obj[key] === 'object' && obj[key] !== null) {
                              const found = findInObject(obj[key], depth + 1);
                              if (found) return found;
                            }
                          }
                          return null;
                        };
                        intent = findInObject(protectedData);
                      } catch (e) {}
                    }
                    if (intent) {
                      setClientSecret(intent);
                      setError(null);
                    } else {
                      setError('No payment intent found in transaction. The booking may need to be accepted first.');
                    }
                  } catch (err) {
                    setError('Failed to refresh transaction data: ' + (err.message || err));
                  }
                  setProcessing(false);
                }}
                style={{ padding: '0.5em 1em', background: '#635bff', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px' }}
              >
                Check for Payment Intent
              </button>
            </div>
          </div>
        )}
      </form>
    );
  }

  const USE_SCAT_FLOW = (process.env.REACT_APP_USE_SCAT_FLOW || 'true') !== 'false';

  return (
    <Page title="Complete Payment">
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div style={{ padding: '2rem' }}>
          <h1>Complete Payment</h1>
          {!transactionId && <p style={{ color: 'red' }}>No transaction ID found in URL.</p>}
          {loading && <p>Loading order details...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {showAgeGate && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent} role="dialog" aria-modal="true" aria-labelledby="ageGateTitle">
                <h3 id="ageGateTitle" style={{ marginTop: 0 }}>Are you 21 or older?</h3>
                <p>Alcohol purchases require age verification. Please confirm you are 21+.</p>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button onClick={handleAgeConfirm} className={styles.paymentButton} style={{ background: '#4caf50' }}>Yes, I am 21+</button>
                  <button onClick={handleAgeDecline} className={styles.refreshButton}>No</button>
                </div>
              </div>
            </div>
          )}
          {transaction && transaction.attributes && (
            <div className={styles.container}>
              <div className={styles.section}>
                <h2 className={styles.heading}>Order Details</h2>
                {(() => {
                  const pd = transaction.attributes.protectedData || {};
                  const event = pd.eventDetails || {};
                  return (
                    <>
                      <div><span className={styles.label}>Date:</span> <span className={styles.value}>{event.date || '-'}</span></div>
                      <div><span className={styles.label}>Time:</span> <span className={styles.value}>{event.startTime || '-'} - {event.endTime || '-'}</span></div>
                      <div><span className={styles.label}>Location:</span> <span className={styles.value}>{event.location || '-'}</span></div>
                      <div><span className={styles.label}>Guest Count:</span> <span className={styles.value}>{event.guestCount || '-'}</span></div>
                    </>
                  );
                })()}
              </div>

              <div className={styles.section}>
                <h3>Order Breakdown</h3>
                {(() => {
                  const pricing = transaction.attributes?.protectedData?.pricing || {};
                  const lineItems = transaction.attributes?.lineItems || [];
                  const rawFoodCost = pricing.totalFoodCost ?? 0;
                  const ingredientDrinkCost = pricing.totalIngredientCost ?? 0;
                  const otherAddOnsCost = pricing.totalOtherAddOnsCost ?? 0;
                  const foodLineItemCost = lineItems
                    .filter(li => li.code === 'line-item/food' && !li.reversal)
                    .reduce((sum, li) => sum + (li.lineTotal?.amount || 0) / 100, 0);
                  const hasCocktails = ingredientDrinkCost > 0 || lineItems.some(li => li.code === 'line-item/cocktail');
                  let effectiveFoodCost = rawFoodCost;
                  let effectiveOtherAddOnsCost = otherAddOnsCost;
                  if (effectiveFoodCost === 0 && foodLineItemCost > 0) {
                    effectiveFoodCost = foodLineItemCost;
                  }
                  if (effectiveFoodCost === 0) {
                    const pdFood = (transaction.attributes?.protectedData?.foodItems || []);
                    if (Array.isArray(pdFood) && pdFood.length > 0) {
                      const pdFoodTotal = pdFood.reduce((sum, fi) => {
                        const qty = fi.quantity || fi.qty || 1;
                        const unit = fi.price || fi.unitPrice || (fi.cost && qty > 0 ? fi.cost / qty : 0);
                        return sum + (unit * qty || 0);
                      }, 0);
                      if (pdFoodTotal > 0) {
                        effectiveFoodCost = pdFoodTotal;
                      }
                    }
                  }
                  if (!hasCocktails && effectiveFoodCost === 0 && effectiveOtherAddOnsCost > 0) {
                    effectiveFoodCost = effectiveOtherAddOnsCost;
                    effectiveOtherAddOnsCost = 0;
                  }
                  if (rawFoodCost === 0 && foodLineItemCost > 0 && effectiveOtherAddOnsCost >= foodLineItemCost) {
                    effectiveOtherAddOnsCost = effectiveOtherAddOnsCost - foodLineItemCost;
                  }
                  try {
                    if (typeof window !== 'undefined' && window?.console) {
                      console.log('[PaymentPage Breakdown Debug]', {
                        rawFoodCost,
                        foodLineItemCost,
                        pricingObject: pricing,
                        effectiveFoodCost,
                        ingredientDrinkCost,
                        effectiveOtherAddOnsCost,
                        hasCocktails,
                        lineItemCodes: lineItems.map(li => li.code)
                      });
                    }
                  } catch (_) {}
                  return (
                    <table className={styles.pricingTable}>
                      <tbody>
                        {effectiveFoodCost > 0 && (
                          <tr><td>Food Total</td><td>${effectiveFoodCost.toFixed(2)}</td></tr>
                        )}
                        {(ingredientDrinkCost + effectiveOtherAddOnsCost) > 0 && (
                          <tr><td>Drink Total</td><td>${(ingredientDrinkCost + effectiveOtherAddOnsCost).toFixed(2)}</td></tr>
                        )}
                        <tr>
                          <td>Preparation &amp; Service</td>
                          <td>
                            {(() => {
                              const lineItems = transaction.attributes?.lineItems || [];
                              const serviceFeeItems = lineItems.filter(item =>
                                (item.code === 'line-item/service-fee' || item.code === 'line-item/cocktail-service-fee') && !item.reversal
                              );
                              const total = serviceFeeItems.reduce((sum, item) => sum + (item.lineTotal?.amount || 0) / 100, 0);
                              return <b>${total.toFixed(2)}</b>;
                            })()}
                          </td>
                        </tr>
                        <tr><td>Ice Cost</td><td>${pricing.iceCost?.toFixed(2) || '0.00'}</td></tr>
                        <tr><td>Glassware Cost</td><td>${pricing.glasswareCost?.toFixed(2) || '0.00'}</td></tr>
                        <tr><td>Booking &amp; Delivery</td><td>${pricing.tenPercentFee?.toFixed(2) || '-'}</td></tr>
                        <tr className={styles.total}><td>Total</td><td>${pricing.grandTotal?.toFixed(2) || '-'}</td></tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              <div className={styles.section}>
                <h3>Payment</h3>
                {hasAlcohol && (
                  <div className={styles.disclosureCard} style={{ marginBottom: '1em' }}>
                    <strong>Alcohol disclosure</strong>
                    <p style={{ margin: '0.35em 0 0 0' }}>
                      Alcohol is sold and fulfilled by {RETAILER_NAME}. Indytend provides delivery services.
                    </p>
                    <p style={{ margin: '0.35em 0 0 0', fontSize: '0.9em' }}>
                      You must be 21+ and present a valid government-issued ID at delivery. Adult signature required.
                    </p>
                  </div>
                )}
                <div className={styles.section} style={{ marginBottom: '1em' }}>
                  <strong>Payment Method:</strong> Credit Card (Stripe)
                </div>
                {transaction.attributes.state?.endsWith('pending-payment') ? (
                  <Elements stripe={stripePromise}>
                    <div style={{ background: '#f3f3f3', padding: '1em', borderRadius: '6px', color: '#222' }}>
                      {hasAlcohol && !idVerified && (
                        <div className={styles.notice} style={{ marginBottom: '0.75em' }}>
                          Please upload a government-issued ID before paying for alcohol orders. <a href="/profile-settings">Upload ID</a>
                        </div>
                      )}
                      <StripePaymentForm
                        amount={Number(transaction.attributes?.protectedData?.pricing?.grandTotal) || 0}
                        transactionId={transaction.id && transaction.id.uuid}
                        transaction={transaction}
                        useScatFlow={USE_SCAT_FLOW}
                        parentSoftSuccess={scatSoftSuccess}
                        registerSoftSuccess={() => setScatSoftSuccess(true)}
                        blocked={(hasAlcohol && (!ageConfirmed || !idVerified))}
                        blockedReasons={[
                          ...(hasAlcohol && !ageConfirmed ? ['Confirm you are 21+ to continue.'] : []),
                          ...(hasAlcohol && !idVerified ? ['Upload a government-issued ID in your Profile Settings.'] : []),
                        ]}
                      />
                      {scatSoftSuccess && !transaction.attributes?.protectedData?.scatCompleted && (
                        <div style={{marginTop:'0.75em',background:'#efffef',padding:'0.75em',border:'1px solid #cce6cc',borderRadius:'4px',fontSize:'0.85rem'}}>
                          <strong>Payment Successful (External SCaT)</strong><br/>
                          Your payment was processed. We're finalizing booking state. You may safely leave this page; it will update shortly.
                        </div>
                      )}
                      {USE_SCAT_FLOW && (
                        <div style={{ marginTop: '0.5em', fontSize: '0.75rem', opacity: 0.8 }}>
                          SCaT flow {process.env.REACT_APP_USE_SCAT_FLOW === 'true' ? 'enabled' : 'flagged via window'}.
                        </div>
                      )}
                    </div>
                  </Elements>
                ) : transaction.attributes.state === 'state/accepted' ? (
                  <div style={{ 
                    color: '#2d8d47', 
                    fontWeight: 'bold', 
                    margin: '1em 0', 
                    padding: '1em',
                    background: '#efffef',
                    border: '1px solid #cce6cc',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <h3 style={{ margin: '0 0 0.5em 0', display: 'flex', alignItems: 'center' }}>
                      <span role="img" aria-label="success" style={{ marginRight: '8px' }}>✔</span>
                      Payment Successfully Processed
                    </h3>
                    <p style={{ margin: '0.5em 0' }}>Your payment has been successfully processed and your booking has been accepted.</p>
                    <p style={{ margin: '0.5em 0', fontSize: '0.9em' }}>A confirmation email has been sent with your booking details.</p>
                    <div style={{ marginTop: '1rem' }}>
                      <button 
                        onClick={() => window.location.href = '/inbox'}
                        className={styles.viewBookingsButton || ''}
                        style={{
                          background: '#4caf50',
                          color: 'white',
                          padding: '0.5em 1em',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        View My Bookings
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: 'red', fontWeight: 'bold', margin: '1em 0' }}>
                    Payment is not available for this transaction state: <span style={{ textTransform: 'capitalize' }}>{transaction.attributes?.state || 'unknown'}</span>.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default PaymentPage;

